import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  timeout,
} from 'rxjs/operators';

export interface CepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge?: string;
  gia?: string;
  ddd?: string;
  siafi?: string;
}

export type BrazilStateOutputDTO = {
  id: number;
  sigla: string;
  nome: string;
}

@Injectable({
  providedIn: 'root',
})
export class CepService {
  private readonly viacepUrl = 'https://viacep.com.br/ws';
  private readonly brasilApiUrl = 'https://brasilapi.com.br/api';

  constructor(private http: HttpClient) {}

  getAddressByZipCode(zip: string): Observable<CepResponse> {
    const cleanZip = zip.replace(/\D/g, '').trim();
    if (!/^\d{8}$/.test(cleanZip)) {
      return throwError('Formato de CEP inválido');
    }
    return this.fetchFromBrasilApi(cleanZip).pipe(
      take(1),
      catchError(() => this.fetchFromViaCep(cleanZip))
    );
  }

  private fetchFromBrasilApi(zip: string): Observable<CepResponse> {
    return this.http.get<any>(`${this.brasilApiUrl}/cep/v2/${zip}`).pipe(
      timeout(3000),
      map((resp) => ({
        cep: resp.cep,
        logradouro: resp.street,
        complemento: resp.complement || '',
        bairro: resp.neighborhood,
        localidade: resp.city,
        uf: resp.state,
      })),
      catchError(() => this.fetchFromViaCep(zip))
    );
  }

  private fetchFromViaCep(zip: string): Observable<CepResponse> {
    return this.http.get<any>(`${this.viacepUrl}/${zip}/json/`).pipe(
      timeout(1000),
      switchMap((resp: any) => {
        if (resp.erro) {
          return throwError('CEP não encontrado ou serviço indisponível');
        }
        return of(resp as CepResponse);
      }),
      catchError(() => throwError('CEP não encontrado ou serviço indisponível'))
    );
  }

  public getBrazillianStateList(): Observable<BrazilStateOutputDTO[]>{
    return this.http.get<BrazilStateOutputDTO[]>(`${this.brasilApiUrl}/ibge/uf/v1`);
  }

  public getBrazillianStatesCity(uf: string): Observable<{nome: string}[]> {
    return this.http.get<{nome: string}[]>(`${this.brasilApiUrl}/ibge/municipios/v1/${uf}?providers=gov`); 
  }
}
